import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Button, DialogActions, DialogContent } from '@mui/material';
import { StyledDialog } from 'common/components/StyledDialog/StyledDialog';
import { StyledDialogFormError } from 'common/components/StyledDialog/StyledDialogFormError';
import { StyledDialogTitle } from 'common/components/StyledDialog/StyledDialogTitle';
import { FormInput } from 'common/form/components/FormInput';
import { fieldIsRequiredValidator } from 'common/form/validators';
import { DialogComponentProps } from 'common/hooks/useDialog';
import { AuthContext } from 'common/providers/AuthContextProvider/AuthContextProvider';
import { useContext, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { styles } from './ChangeDialog.styles';

export interface FormValues {
  name?: string;
}

export type ChangeUsernameDialogProps = DialogComponentProps<void>;

export const ChangeUsernameDialog: React.FC<ChangeUsernameDialogProps> = ({ isOpen, onReject, onResolve }) => {
  const { t } = useTranslation();
  const { updateAttributes, userData } = useContext(AuthContext);

  const schema: yup.SchemaOf<FormValues> = yup.object({
    name: fieldIsRequiredValidator(),
  });

  const formMethods = useForm<FormValues>({
    defaultValues: {
      name: userData?.user.attributes.preferred_username,
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = formMethods.handleSubmit(async (data) => {
    if (data.name) {
      setError('');
      setLoading(true);
      const resp = await updateAttributes({
        preferred_username: data.name,
      });

      if (resp) {
        setError(resp.formError?.message ?? t('common:somethingWentWrong'));
      } else {
        onResolve();
      }
      setLoading(false);
    }
  });

  return (
    <FormProvider {...formMethods}>
      <StyledDialog maxWidth="sm" onClose={() => onReject()} open={isOpen}>
        <StyledDialogTitle textAlign="left">{t('settings.changeDialog.username.title')}</StyledDialogTitle>
        <Box component="form" noValidate onSubmit={handleSubmit}>
          <DialogContent>
            <FormInput
              fullWidth
              label={t('settings.changeDialog.username.label')}
              name="name"
              placeholder={t('settings.changeDialog.placeholder.username')}
              sx={styles.input}
              type="text"
            />
          </DialogContent>
          <DialogActions>
            <Button color="secondaryButton" onClick={() => onReject()}>
              {t('settings.changeDialog.cancel')}
            </Button>
            <LoadingButton loading={loading} type="submit">
              {t('settings.changeDialog.save')}
            </LoadingButton>
          </DialogActions>
          {!!error && !loading && <StyledDialogFormError content={error} title={t('settings.changeDialog.error')} />}
        </Box>
      </StyledDialog>
    </FormProvider>
  );
};
